import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../features/auth/AuthProvider.js";
import * as systemApi from "../services/system-api.js";
import * as tenantsApi from "../services/tenants-api.js";
import * as authApi from "../services/auth-api.js";
import type { SystemInfo, TenantPodPolicy } from "../types/api.js";
import { ApiClientError } from "../services/api-client.js";

export function ProfilePage(): JSX.Element {
  const { user, token, status, bootstrapped, refreshUser, logout, setFlash } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [podPolicy, setPodPolicy] = useState<TenantPodPolicy | null>(null);
  const [podPolicyError, setPodPolicyError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void systemApi
      .fetchSystemInfo()
      .then((i) => {
        if (!cancelled) setInfo(i);
      })
      .catch((e) => {
        if (!cancelled) setInfoError(e instanceof ApiClientError ? e.message : "Error al cargar system/info");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!token || !user?.tenantId) {
      setPodPolicy(null);
      setPodPolicyError(null);
      return;
    }
    let cancelled = false;
    setPodPolicyError(null);
    void tenantsApi
      .fetchTenantPodSettings(token, user.tenantId)
      .then((r) => {
        if (!cancelled) setPodPolicy(r.settings);
      })
      .catch((e) => {
        if (!cancelled) {
          setPodPolicy(null);
          setPodPolicyError(e instanceof ApiClientError ? e.message : "No se pudo cargar la política POD.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.tenantId]);

  async function onRefreshMe() {
    setRefreshing(true);
    try {
      await refreshUser();
    } finally {
      setRefreshing(false);
    }
  }

  async function onChangePassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPwError(null);
    if (newPassword !== newPassword2) {
      setPwError("Las contraseñas nuevas no coinciden.");
      return;
    }
    setPwSubmitting(true);
    try {
      await authApi.changePasswordRequest(currentPassword, newPassword);
      setFlash("Contraseña actualizada. Inicie sesión de nuevo.");
      logout();
      navigate("/login", { replace: true });
    } catch (err) {
      setPwError(err instanceof ApiClientError ? err.message : "No se pudo cambiar la contraseña.");
    } finally {
      setPwSubmitting(false);
    }
  }

  const tokenPreview = token ? `${token.slice(0, 12)}… (${token.length} caracteres)` : "—";

  return (
    <div className="profile-page">
      <header className="page-header">
        <h1>Perfil y sesión</h1>
        <p className="text-muted">Validación del contexto devuelto por <code>GET /api/v1/auth/me</code>.</p>
      </header>

      <section className="card">
        <h2>Usuario autenticado</h2>
        <ul className="meta-list">
          <li>
            <strong>Id:</strong> <code>{user?.id}</code>
          </li>
          <li>
            <strong>Correo:</strong> {user?.email}
          </li>
          <li>
            <strong>Rol:</strong> <code>{user?.role}</code>
          </li>
          <li>
            <strong>TenantId:</strong>{" "}
            {user?.tenantId ? <code>{user.tenantId}</code> : <span className="text-muted">null</span>}
          </li>
        </ul>
        <div className="actions-row">
          <button type="button" className="btn btn-secondary" onClick={() => void onRefreshMe()} disabled={refreshing}>
            {refreshing ? "Actualizando…" : "Actualizar datos desde API"}
          </button>
        </div>
        <p className="text-muted small">
          Estado interno auth: bootstrapped={String(bootstrapped)}, status={status}
        </p>
      </section>

      <section className="card">
        <h2>Cambiar contraseña</h2>
        <p className="text-muted small">
          Requiere la contraseña actual. Al guardar se revocan todas las sesiones en el servidor; deberá entrar de
          nuevo.
        </p>
        <form onSubmit={(e) => void onChangePassword(e)} className="login-form" style={{ maxWidth: "24rem" }}>
          <label className="field">
            <span>Contraseña actual</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Nueva contraseña (mín. 8)</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="field">
            <span>Confirmar nueva contraseña</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              minLength={8}
              required
            />
          </label>
          {pwError ? <p className="form-error">{pwError}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={pwSubmitting}>
            {pwSubmitting ? "Guardando…" : "Guardar nueva contraseña"}
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Sesión (desarrollo)</h2>
        <p className="text-muted small">
          El token se guarda en <code>sessionStorage</code> bajo clave dedicada al portal. Cierra la pestaña o usa
          «Cerrar sesión» para limpiarlo.
        </p>
        <ul className="meta-list">
          <li>
            <strong>Token (vista previa):</strong> <code className="break-all">{tokenPreview}</code>
          </li>
        </ul>
      </section>

      {user?.tenantId ? (
        <section className="card">
          <h2>Política POD del tenant (solo lectura)</h2>
          <p className="text-muted small">
            Valores desde <code>GET /api/v1/tenants/…/settings</code>. La edición se hace en el panel de administración.
          </p>
          {podPolicyError ? <p className="form-error">{podPolicyError}</p> : null}
          {podPolicy ? (
            <ul className="meta-list">
              <li>
                <strong>Exigir POD para entrega:</strong> {podPolicy.requiresPodForDelivery ? "sí" : "no"}
              </li>
              <li>
                <strong>Exigir aceptación del POD:</strong> {podPolicy.requiresAcceptedPod ? "sí" : "no"}
              </li>
              <li>
                <strong>Enforcement antes de entregar:</strong> {podPolicy.enforcePodBeforeDeliver ? "sí" : "no"}
              </li>
            </ul>
          ) : !podPolicyError ? (
            <p className="text-muted">Cargando…</p>
          ) : null}
        </section>
      ) : null}

      <section className="card">
        <h2>Entorno API</h2>
        {infoError ? <p className="form-error">{infoError}</p> : null}
        {info ? (
          <ul className="meta-list">
            <li>
              <strong>Producto:</strong> {info.product}
            </li>
            <li>
              <strong>Versión API:</strong> {info.version} (fase {info.phase})
            </li>
            <li>
              <strong>Persistencia:</strong> {info.persistence}
            </li>
          </ul>
        ) : !infoError ? (
          <p className="text-muted">Cargando…</p>
        ) : null}
      </section>
    </div>
  );
}
